/**
 * InlineChart — renders a Recharts chart from a Claude-provided spec.
 * Supports bar, line, area, and pie chart types.
 */
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'

const DEFAULT_COLORS = ['#6366f1', '#22d3ee', '#f59e0b', '#ef4444', '#10b981', '#8b5cf6', '#f97316', '#ec4899']

function formatValue(val) {
  if (typeof val !== 'number') return val
  if (Math.abs(val) >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`
  if (Math.abs(val) >= 1_000) return `${(val / 1_000).toFixed(0)}K`
  return val.toFixed(2)
}

export default function InlineChart({ spec }) {
  const {
    chart_type,
    title,
    data,
    data_keys = [],
    x_axis_key = 'name',
    y_axis_label,
    stacked = false,
  } = spec

  if (!data?.length) {
    return <div className="text-ink-muted text-sm italic">No data to display</div>
  }

  const commonProps = {
    data,
    margin: { top: 5, right: 20, bottom: 5, left: 10 },
  }

  const axisProps = {
    xAxis: <XAxis dataKey={x_axis_key} tick={{ fontSize: 11, fill: 'var(--color-ink-muted)' }} />,
    yAxis: <YAxis tick={{ fontSize: 11, fill: 'var(--color-ink-muted)' }} tickFormatter={formatValue} label={y_axis_label ? { value: y_axis_label, angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: 'var(--color-ink-muted)' } } : undefined} />,
    grid: <CartesianGrid strokeDasharray="3 3" stroke="var(--color-edge-subtle)" />,
    tooltip: <Tooltip contentStyle={{ backgroundColor: 'var(--color-surface-tertiary)', border: '1px solid var(--color-edge)', borderRadius: 8, fontSize: 12 }} formatter={formatValue} />,
    legend: <Legend wrapperStyle={{ fontSize: 11 }} />,
  }

  function renderChart() {
    switch (chart_type) {
      case 'bar':
        return (
          <BarChart {...commonProps}>
            {axisProps.grid}
            {axisProps.xAxis}
            {axisProps.yAxis}
            {axisProps.tooltip}
            {data_keys.length > 1 && axisProps.legend}
            {data_keys.map((dk, i) => (
              <Bar
                key={dk.key}
                dataKey={dk.key}
                fill={dk.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length]}
                name={dk.label || dk.key}
                stackId={stacked ? 'stack' : undefined}
                radius={[2, 2, 0, 0]}
              />
            ))}
          </BarChart>
        )

      case 'line':
        return (
          <LineChart {...commonProps}>
            {axisProps.grid}
            {axisProps.xAxis}
            {axisProps.yAxis}
            {axisProps.tooltip}
            {data_keys.length > 1 && axisProps.legend}
            {data_keys.map((dk, i) => (
              <Line
                key={dk.key}
                type="monotone"
                dataKey={dk.key}
                stroke={dk.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length]}
                name={dk.label || dk.key}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        )

      case 'area':
        return (
          <AreaChart {...commonProps}>
            {axisProps.grid}
            {axisProps.xAxis}
            {axisProps.yAxis}
            {axisProps.tooltip}
            {data_keys.length > 1 && axisProps.legend}
            {data_keys.map((dk, i) => (
              <Area
                key={dk.key}
                type="monotone"
                dataKey={dk.key}
                fill={dk.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length]}
                stroke={dk.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length]}
                name={dk.label || dk.key}
                fillOpacity={0.3}
                stackId={stacked ? 'stack' : undefined}
              />
            ))}
          </AreaChart>
        )

      case 'pie':
        return (
          <PieChart>
            {axisProps.tooltip}
            <Pie
              data={data}
              dataKey={data_keys[0]?.key || 'value'}
              nameKey={x_axis_key}
              cx="50%"
              cy="50%"
              outerRadius={80}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              labelLine={{ stroke: 'var(--color-ink-muted)' }}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={data[i]?.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length]} />
              ))}
            </Pie>
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        )

      default:
        return <div className="text-ink-muted text-sm">Unsupported chart type: {chart_type}</div>
    }
  }

  return (
    <div className="my-3">
      {title && <div className="text-sm font-medium text-ink mb-2">{title}</div>}
      <div className="bg-surface-tertiary/50 rounded-lg p-3 border border-edge-subtle">
        <ResponsiveContainer width="100%" height={200}>
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  )
}
